import React, { Component } from "react";
import "../styles/referralCard.scss";
import Info from "./info";
import PrimaryActionButton from "../../Shared/PrimaryActionButton/PrimaryActionButton";
import SecondaryActionButton from "../../Shared/SecondaryActionButton/SecondaryActionButton";
import DownloadIcon from "../icons/download.svg";
import { toast } from "react-toastify";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { referralService } from "../services/referralService";

export default class ReferralCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      actionType: null,
      error: null,
      confirmDialogOpen: false,
      pendingAction: null,
    };
  }

  // Dialog handlers
  handleActionClick = (status) => {
    this.setState({
      confirmDialogOpen: true,
      pendingAction: status,
    });
  };

  handleConfirmDialogClose = () => {
    this.setState({
      confirmDialogOpen: false,
      pendingAction: null,
    });
  };

  handleConfirmAction = () => {
    const { pendingAction } = this.state;
    this.setState({ confirmDialogOpen: false }, () => {
      if (pendingAction) {
        this.onStatusChange(pendingAction);
      }
    });
  };

  // API actions
  onStatusChange = async (status) => {
    this.setState({ loading: true, actionType: status, error: null });

    try {
      const { campaign_id } = this.props.referralInfo;
      const response = await referralService.changeStatus(campaign_id, status);

      // Check if the response is successful
      if (response.status >= 200 && response.status < 300) {
        toast.success(
          `Campaign ${
            status === "deactivated" ? "deactivated" : "disbursed"
          } successfully`
        );

        // If there's a callback for refreshing data, call it
        if (this.props.onStatusChanged) {
          this.props.onStatusChanged(campaign_id, status);
        }
      } else {
        throw new Error(`Failed to ${status} campaign`);
      }
    } catch (error) {
      console.error(`Error during ${status} operation:`, error);
      this.handleApiError(error, `Failed to ${status} campaign`);
    } finally {
      this.setState({ loading: false, actionType: null });
    }
  };

  onReportDownload = async () => {
    try {
      const { campaign_id, campaign_name } = this.props.referralInfo;
      this.setState({ loading: true, actionType: "report" });

      await referralService.downloadReport(campaign_id, campaign_name);
      toast.success("Report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      this.handleApiError(error, "Failed to download report");
    } finally {
      this.setState({ loading: false, actionType: null });
    }
  };

  // Helper methods
  handleApiError = (error, defaultMessage) => {
    let errorMessage = defaultMessage;

    if (error.response && error.response.data) {
      if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    this.setState({ error: errorMessage });
    toast.error(errorMessage);
  };

  renderConfirmationDialog() {
    const { confirmDialogOpen, pendingAction } = this.state;
    const { campaign_name } = this.props.referralInfo || {};

    const actionText =
      pendingAction === "deactivated" ? "deactivate" : "disburse";

    return (
      <Dialog
        open={confirmDialogOpen}
        onClose={this.handleConfirmDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <p className="referral-card-confirm-message">
            Are you sure you want to {actionText} this campaign?
          </p>
          <p className="referral-card-campaign-name">
            {campaign_name ? `${campaign_name}` : ""}?
          </p>
          <p className="referral-card-warning-message">
            This action cannot be undone.
          </p>
        </DialogContent>
        <DialogActions style={{ marginRight: "5%" }}>
          <SecondaryActionButton
            onClick={this.handleConfirmDialogClose}
            title="Cancel"
            style={{ width: 200 }}
          />

          <PrimaryActionButton
            onClick={this.handleConfirmAction}
            className="referral-card-action-btn"
            title="Confirm"
            style={{ width: 200 }}
          >
            Confirm
          </PrimaryActionButton>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    if (!this.props.referralInfo) return null;

    const {
      campaign_name,
      campaign_id,
      campaign_duration,
      services_considered,
      is_active,
      is_disbursed,
    } = this.props.referralInfo;

    const { loading, actionType, error } = this.state;

    // Determine if buttons should be disabled
    const isDeactivateDisabled = loading || !is_active;
    const isDisburseDisabled = loading || is_disbursed || !is_active;

    return (
      <div className={`referral-card ${error ? "has-error" : ""}`}>
        <p className="title">{campaign_name || "Unnamed Campaign"}</p>

        {campaign_id && (
          <Info header="Campaign ID" body={campaign_id} copyMsg="Campaign ID" />
        )}
        {campaign_duration && (
          <Info header="Duration" body={campaign_duration} />
        )}
        {services_considered && (
          <Info header="Services" body={services_considered} />
        )}

        {error && <div className="referral-card-error-message">{error}</div>}

        <div className="referral-card-actions">
          {/* <PrimaryActionButton
            title="Report"
            className="referral-card-action-btn"
            leftIconSrc={DownloadIcon}
            onClick={this.onReportDownload}
            loading={actionType === "report" && loading}
          /> */}

          <PrimaryActionButton
            title={
              !is_active
                ? "Deactivated"
                : actionType === "deactivated" && loading
                ? "Deactivating..."
                : "Deactivate"
            }
            className="referral-card-action-btn"
            onClick={() => this.handleActionClick("deactivated")}
            disabled={isDeactivateDisabled}
            loading={actionType === "deactivated" && loading}
          >
            {actionType === "deactivated" && loading && (
              <CircularProgress
                size={16}
                color="inherit"
                style={{ marginRight: 8 }}
              />
            )}
          </PrimaryActionButton>

          <SecondaryActionButton
            title={
              is_disbursed
                ? "Disbursed"
                : actionType === "disbursed" && loading
                ? "Disbursing..."
                : "Disburse"
            }
            className="referral-card-action-btn"
            onClick={() => this.handleActionClick("disbursed")}
            disabled={isDisburseDisabled}
            loading={actionType === "disbursed" && loading}
          >
            {actionType === "disbursed" && loading && (
              <CircularProgress
                size={16}
                color="inherit"
                style={{ marginRight: 8 }}
              />
            )}
          </SecondaryActionButton>
        </div>

        {this.renderConfirmationDialog()}
      </div>
    );
  }
}
